/**
 * Hook para monitoramento da fila PGMQ de contratos (PDF + assinatura)
 * 
 * Usa as RPCs get_contract_queue_stats_pgmq, get_contract_queue_items_pgmq,
 * get_contract_queue_volume_pgmq e get_contract_queue_slowest_pgmq
 */

import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

// ============================================================================
// TIPOS E INTERFACES
// ============================================================================

/** Interface para estatísticas da fila de contratos */
export interface ContractQueueStats {
  pending: number;
  processing: number;
  completed24h: number;
  failed24h: number;
  dlqCount: number;
  successRate: number;
  avgProcessingTime: number;
}

/** Interface para item da fila de contratos */
export interface ContractQueueItem {
  msg_id: number;
  read_ct: number;
  enqueued_at: string;
  vt: string;
  message: Record<string, unknown>;
  status: 'pending' | 'processing' | 'archived' | 'dlq';
  tenant_id: string | null;
  source: 'queue' | 'archive' | 'dlq';
  // Campos enriquecidos
  tenant_name?: string;
  contract_title?: string;
  action?: string;
  processing_time?: number;
}

/** Interface para volume por hora */
export interface ContractQueueVolume {
  hour: string;
  completed: number;
  failed: number;
}

/** Interface para alerta de saúde */
export interface ContractQueueAlert {
  id: string;
  type: 'warning' | 'error';
  message: string;
  count?: number;
}

/** Interface para item lento */
export interface ContractQueueSlowestItem {
  msg_id: number;
  read_ct: number;
  enqueued_at: string;
  completed_at: string | null;
  processing_time_ms: number;
  processing_time_formatted: string;
  status: 'pending' | 'processing' | 'archived';
  tenant_id: string | null;
  tenant_name?: string;
  action?: string;
  contract_title?: string;
  source: 'queue' | 'archive';
  message?: Record<string, unknown>;
}

// ============================================================================
// INTERFACES DE RESPOSTA DAS RPCS
// ============================================================================

interface StatsRpcResponse {
  pending_count: number;
  processing_count: number;
  completed_count: number;
  failed_count: number;
  dlq_count: number;
  avg_latency_ms: number;
}

interface ItemRpcResponse {
  msg_id: number;
  read_ct: number;
  enqueued_at: string;
  vt: string;
  message: Record<string, unknown>;
  status: string;
  tenant_id: string | null;
  source: string;
}

interface VolumeRpcResponse {
  hour_label: string;
  completed_count: number;
  failed_count: number;
}

interface SlowestRpcResponse {
  msg_id: number;
  read_ct: number;
  enqueued_at: string;
  completed_at: string | null;
  processing_time_ms: number;
  status: string;
  tenant_id: string | null;
  message: Record<string, unknown>;
  source: string;
}

// ============================================================================
// HELPERS
// ============================================================================

/** Formata tempo de processamento em string legível */
function formatProcessingTimeMs(ms: number): string {
  const seconds = ms / 1000;
  if (seconds < 60) return `${seconds.toFixed(1)}s`;
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = Math.floor(seconds % 60);
  if (minutes < 60) return `${minutes}m ${remainingSeconds}s`;
  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;
  return `${hours}h ${remainingMinutes}m`;
}

// ============================================================================
// HOOKS
// ============================================================================

/**
 * Hook para buscar estatísticas da fila de contratos
 */
export function useContractQueueStats() {
  return useQuery({
    queryKey: ['contract-queue-stats'],
    queryFn: async () => {
      const { data, error } = await supabase.rpc('get_contract_queue_stats_pgmq');
      if (error) throw error;

      const stats = (data as unknown as StatsRpcResponse[])?.[0];
      if (!stats) {
        return {
          pending: 0, processing: 0, completed24h: 0, failed24h: 0,
          dlqCount: 0, successRate: 100, avgProcessingTime: 0
        } as ContractQueueStats;
      }

      const total = stats.completed_count + stats.failed_count;
      const successRate = total > 0 ? (stats.completed_count / total) * 100 : 100;

      return {
        pending: stats.pending_count || 0,
        processing: stats.processing_count || 0,
        completed24h: stats.completed_count || 0,
        failed24h: stats.failed_count || 0,
        dlqCount: stats.dlq_count || 0,
        successRate,
        avgProcessingTime: (stats.avg_latency_ms || 0) / 1000
      } as ContractQueueStats;
    },
    refetchInterval: 30000,
  });
}

/**
 * Hook para buscar itens da fila de contratos
 */
export function useContractQueueItems(statusFilter: string = 'all', limit: number = 200) {
  return useQuery({
    queryKey: ['contract-queue-items', statusFilter, limit],
    queryFn: async () => {
      const { data, error } = await supabase.rpc('get_contract_queue_items_pgmq', {
        p_status: statusFilter,
        p_limit: limit
      });
      if (error) throw error;
      if (!data || data.length === 0) return [];

      const rawItems = data as unknown as ItemRpcResponse[];

      // Enriquecimento de tenant names
      const tenantIds = new Set<string>();
      rawItems.forEach(item => { if (item.tenant_id) tenantIds.add(item.tenant_id); });

      let tenantMap = new Map<string, string>();
      if (tenantIds.size > 0) {
        const { data: tenants } = await supabase
          .from('tenants').select('id, name').in('id', Array.from(tenantIds));
        tenantMap = new Map((tenants || []).map((t: { id: string; name: string }) => [t.id, t.name]));
      }

      return rawItems.map(item => {
        let processingTime: number | undefined;
        if (item.source === 'archive' && item.enqueued_at && item.vt) {
          processingTime = (new Date(item.vt).getTime() - new Date(item.enqueued_at).getTime()) / 1000;
        }
        return {
          msg_id: item.msg_id,
          read_ct: item.read_ct,
          enqueued_at: item.enqueued_at,
          vt: item.vt,
          message: item.message,
          status: item.status as ContractQueueItem['status'],
          tenant_id: item.tenant_id,
          source: item.source as ContractQueueItem['source'],
          tenant_name: item.tenant_id ? tenantMap.get(item.tenant_id) || 'N/A' : 'N/A',
          action: (item.message?.action as string) || (item.message?.type as string) || 'unknown',
          contract_title: (item.message?.contract_title as string) || (item.message?.title as string) || '',
          processing_time: processingTime,
        };
      }) as ContractQueueItem[];
    },
    refetchInterval: 30000,
  });
}

/**
 * Hook para buscar volume de processamento por hora
 */
export function useContractQueueVolume(hours: number = 24) {
  return useQuery({
    queryKey: ['contract-queue-volume', hours],
    queryFn: async () => {
      const { data, error } = await supabase.rpc('get_contract_queue_volume_pgmq', { hours_ago: hours });
      if (error) throw error;
      return ((data || []) as unknown as VolumeRpcResponse[]).map(item => ({
        hour: item.hour_label,
        completed: item.completed_count || 0,
        failed: item.failed_count || 0
      })) as ContractQueueVolume[];
    },
    refetchInterval: 60000,
  });
}

/**
 * Hook para gerar alertas de saúde da fila de contratos
 */
export function useContractQueueAlerts() {
  return useQuery({
    queryKey: ['contract-queue-alerts'],
    queryFn: async () => {
      const alerts: ContractQueueAlert[] = [];
      const { data: statsData } = await supabase.rpc('get_contract_queue_stats_pgmq');
      const stats = (statsData as unknown as StatsRpcResponse[])?.[0];
      if (!stats) return alerts;

      if (stats.pending_count > 50) {
        alerts.push({ id: 'high-pending', type: 'warning', message: `${stats.pending_count} contratos pendentes na fila`, count: stats.pending_count });
      }
      if (stats.processing_count > 20) {
        alerts.push({ id: 'high-processing', type: 'warning', message: `${stats.processing_count} contratos em processamento (podem estar stuck)`, count: stats.processing_count });
      }
      if (stats.dlq_count > 0) {
        alerts.push({ id: 'dlq-items', type: 'error', message: `${stats.dlq_count} contratos na DLQ (falhas permanentes)`, count: stats.dlq_count });
      }
      const total = stats.completed_count + stats.failed_count;
      const successRate = total > 0 ? (stats.completed_count / total) * 100 : 100;
      if (successRate < 90 && total > 10) {
        alerts.push({ id: 'low-success-rate', type: 'error', message: `Taxa de sucesso baixa: ${successRate.toFixed(1)}%`, count: stats.failed_count });
      }
      if (stats.avg_latency_ms > 60000) {
        alerts.push({ id: 'high-latency', type: 'warning', message: `Latência média alta: ${(stats.avg_latency_ms / 1000).toFixed(0)}s` });
      }
      return alerts;
    },
    refetchInterval: 30000,
  });
}

/**
 * Hook para buscar os TOP N itens mais lentos
 */
export function useContractQueueSlowestItems(limit: number = 20) {
  return useQuery({
    queryKey: ['contract-queue-slowest', limit],
    queryFn: async () => {
      const { data, error } = await supabase.rpc('get_contract_queue_slowest_pgmq', { p_limit: limit });
      if (error) throw error;
      if (!data || data.length === 0) return [];

      const rawItems = data as unknown as SlowestRpcResponse[];

      // Enriquecimento de tenant names
      const tenantIds = new Set<string>();
      rawItems.forEach(item => { if (item.tenant_id) tenantIds.add(item.tenant_id); });

      let tenantMap = new Map<string, string>();
      if (tenantIds.size > 0) {
        const { data: tenants } = await supabase
          .from('tenants').select('id, name').in('id', Array.from(tenantIds));
        tenantMap = new Map((tenants || []).map((t: { id: string; name: string }) => [t.id, t.name]));
      }

      return rawItems.map(item => ({
        msg_id: item.msg_id,
        read_ct: item.read_ct || 0,
        enqueued_at: item.enqueued_at,
        completed_at: item.completed_at,
        processing_time_ms: item.processing_time_ms,
        processing_time_formatted: formatProcessingTimeMs(item.processing_time_ms),
        status: item.status as ContractQueueSlowestItem['status'],
        tenant_id: item.tenant_id,
        tenant_name: item.tenant_id ? tenantMap.get(item.tenant_id) || 'N/A' : 'N/A',
        action: (item.message?.action as string) || (item.message?.type as string) || 'unknown',
        contract_title: (item.message?.contract_title as string) || (item.message?.title as string) || '',
        source: item.source as ContractQueueSlowestItem['source'],
        message: item.message || {},
      })) as ContractQueueSlowestItem[];
    },
    refetchInterval: 30000,
  });
}
